import { JobData } from '@/types/chat';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Format ISO date to DD/MM/YYYY without timezone offset issues
function formatDateBR(isoDate: string): string {
  // Parse ISO string directly to avoid timezone conversion
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return isoDate;
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

interface PreviewPanelProps {
  jobData?: JobData;
}

export function PreviewPanel({ jobData }: PreviewPanelProps) {
  return (
    <div className="h-full overflow-y-auto p-4 bg-gray-50">
      <Card>
        <CardHeader>
          <CardTitle>Preview da Vaga</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-gray-700">Título</label>
            <p className="mt-1 text-gray-900">
              {jobData?.title || <span className="text-gray-400 italic">Não definido</span>}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-gray-700">Descrição</label>
            <p className="mt-1 text-gray-900 whitespace-pre-wrap">
              {jobData?.description || <span className="text-gray-400 italic">Não definida</span>}
            </p>
          </div>

          {/* Requirements */}
          <div>
            <label className="text-sm font-medium text-gray-700">Requisitos</label>
            {jobData?.requirements && jobData.requirements.length > 0 ? (
              <ul className="mt-1 list-disc list-inside space-y-1">
                {jobData.requirements.map((req, idx) => (
                  <li key={idx} className="text-gray-900">{req}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-gray-400 italic">Nenhum requisito definido</p>
            )}
          </div>

          {/* Nice to Have */}
          <div>
            <label className="text-sm font-medium text-gray-700">Diferenciais</label>
            {jobData?.niceToHave && jobData.niceToHave.length > 0 ? (
              <ul className="mt-1 list-disc list-inside space-y-1">
                {jobData.niceToHave.map((item, idx) => (
                  <li key={idx} className="text-gray-900">{item}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-gray-400 italic">Nenhum diferencial definido</p>
            )}
          </div>

          {/* Evaluation Criteria */}
          <div>
            <label className="text-sm font-medium text-gray-700">Critérios de Avaliação</label>
            {jobData?.evaluationCriteria && jobData.evaluationCriteria.length > 0 ? (
              <div className="mt-1 space-y-2">
                {jobData.evaluationCriteria.map((criteria, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{criteria.name}</p>
                      <p className="text-sm text-gray-600">{criteria.description}</p>
                    </div>
                    <Badge variant="secondary">{(criteria.weight * 100).toFixed(0)}%</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-gray-400 italic">Nenhum critério definido</p>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Data de Abertura</label>
              <p className="mt-1 text-gray-900">
                {jobData?.openedAt ? formatDateBR(jobData.openedAt) : <span className="text-gray-400 italic">Não definida</span>}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Data de Validade</label>
              <p className="mt-1 text-gray-900">
                {jobData?.expiresAt ? formatDateBR(jobData.expiresAt) : <span className="text-gray-400 italic">Não definida</span>}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
